// Lib imports
import React, { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

// utils
import map from 'lodash/map';
import values from 'lodash/values';

// Types
import { AccessControlType } from 'ducks/merchantDucks/accesses/actions';
import { PerAssetEmbedCodeOptions, GlobalEmbedCodeOptions } from 'ducks/merchantDucks/embedding/types';
import RootState from 'ducks/RootState';

// Actions
import { setPerAssetEmbedCodeObject, setGlobalEmbedCodeObject } from 'ducks/merchantDucks/embedding/actions';

// Selectors
import { getGlobalOption, getPerAssetOption } from 'ducks/merchantDucks/embedding/selectors';
import { getUserApplications } from 'ducks/merchantDucks/audience/selectors';
import { getItemDetails } from 'ducks/merchantDucks/items/selectors';

// Components
import { Grid, Option, DefaultOption, Button, Switch } from '@inplayer-org/inplayer-ui';
import ExtendedDropdown from 'components/ExtendedDropdown';

const { Container, Cell } = Grid;

const CellWrapper = styled(Cell)`
  margin-left: 2rem;
  display: inline-flex;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

type DropdownOptions = {
  defaultOption: DefaultOption;
  options: Array<Option>;
};

const EmbedSettings: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  // State
  const [appSsoEnabled, setAppSsoEnabled] = useState(false);

  // Selectors
  const {
    id: assetId,
    accessFees = [],
    access_control_type: { id: accessControlTypeId },
  } = useSelector(getItemDetails);
  const brandingCollection = useSelector((state: RootState) => state.branding.collection);
  const brandingId = useSelector(getPerAssetOption(PerAssetEmbedCodeOptions.BRANDING_ID));
  const userApplications = useSelector(getUserApplications);
  const useSSO = useSelector(getGlobalOption(GlobalEmbedCodeOptions.USE_SSO));
  const disableVoucher = useSelector(getPerAssetOption(PerAssetEmbedCodeOptions.DISABLE_VOUCHER));
  const preselectedFeeId = useSelector(getPerAssetOption(PerAssetEmbedCodeOptions.PRESELECTED_FEE_ID));
  const appId = useSelector(getGlobalOption(GlobalEmbedCodeOptions.OAUTH_APP_KEY));

  useEffect(() => {
    const selectedApp = values(userApplications).find((app) => app.id === appId);
    // const appSsoEnabled = selectedApp ? selectedApp.sso : false,
    setAppSsoEnabled(selectedApp ? selectedApp.sso : false);
  });

  const onBrandingThemeSelected = (id: string) => {
    const pickedTheme = brandingCollection.find((theme: { id: number }) => {
      return theme.id.toString() === id;
    });

    dispatch(setPerAssetEmbedCodeObject(PerAssetEmbedCodeOptions.BRANDING_ID, pickedTheme ? pickedTheme.id : 0));
  };

  const toggleDisableVoucher = (checked: boolean) => {
    dispatch(setPerAssetEmbedCodeObject(PerAssetEmbedCodeOptions.DISABLE_VOUCHER, !checked));
  };

  const onOauthAppSelected = (id: string) => {
    const pickedApp = values(userApplications).find((app) => {
      return app.id === id;
    });

    setAppSsoEnabled(pickedApp ? pickedApp.sso : false);

    dispatch(setGlobalEmbedCodeObject(GlobalEmbedCodeOptions.USE_SSO, false));
    dispatch(setGlobalEmbedCodeObject(GlobalEmbedCodeOptions.OAUTH_APP_KEY, pickedApp ? pickedApp.id : ''));
  };

  const onAccessFeeSelected = (id: string) => {
    const pickedAccessFee = accessFees.find((fee: { id: number }) => {
      return fee.id.toString() === id;
    });

    dispatch(
      setPerAssetEmbedCodeObject(PerAssetEmbedCodeOptions.PRESELECTED_FEE_ID, pickedAccessFee ? pickedAccessFee.id : 0)
    );
  };

  const onToggleUseSSO = (checked: boolean) => {
    dispatch(setGlobalEmbedCodeObject(GlobalEmbedCodeOptions.USE_SSO, checked));
  };

  const parseBrandingThemes = (themes: Array<any>): DropdownOptions => {
    const defaultOption: DefaultOption = { displayName: 'none' };

    const options = map(themes, (theme: any) => {
      return {
        value: theme.id.toString(),
        displayName: theme.brand_name,
      };
    });

    return { defaultOption, options };
  };

  const parseUserApplications = (applications: any): DropdownOptions => {
    const defaultOption: DefaultOption = { displayName: 'none' };

    const options = map(applications, (app: any) => {
      return { value: app.id, displayName: app.name };
    });

    return { defaultOption, options };
  };

  const parceAccessPrices = (accessPrices: any): DropdownOptions => {
    const defaultOption: DefaultOption = { displayName: 'none' };

    const options = map(accessPrices, (fee: any) => {
      return { value: fee.id, displayName: fee.description };
    });

    return { defaultOption, options };
  };

  const navigateToAdvancedEmbedding = (locationState: Record<string, any>) => () => {
    history.push(`/assets/${assetId}/advanced-embedding`, locationState);
  };

  // Render options for select
  const { defaultOption: noneTheme, options: themes } = parseBrandingThemes(brandingCollection);
  const { defaultOption: noneApp, options: apps } = parseUserApplications(userApplications);
  const { defaultOption: noneAccessFee, options: accessPrices } = parceAccessPrices(accessFees);

  // Asset types
  const isPaidAsset = accessControlTypeId === AccessControlType.PAID;
  const isDataCaptureAsset = accessControlTypeId === AccessControlType.DATA;
  const isCodeAndAuthAsset = accessControlTypeId === AccessControlType.CODEAUTH;

  const displayOauthSelect = !!apps.length && (isPaidAsset || isDataCaptureAsset || isCodeAndAuthAsset);
  const displayAccessFeeSelect = accessFees && !!accessFees.length && isPaidAsset;

  return (
    <Container columns="20% 25% 1fr 1fr" rowGap="1rem">
      {!!themes.length && (
        <>
          <Cell left={1} middle>
            Branding theme:
          </Cell>
          <Cell left={2}>
            <ExtendedDropdown
              tag="branding_theme"
              fullWidth
              modifiers={['fontSizeMedium']}
              defaultOption={noneTheme}
              options={themes}
              value={brandingId ? brandingId.toString() : ''}
              onChange={onBrandingThemeSelected}
            />
          </Cell>
          <CellWrapper>
            <Switch
              tag="voucher_use"
              id="disableVoucher"
              label={`Voucher use ${disableVoucher ? 'disabled' : 'enabled'}`}
              checked={!disableVoucher}
              onChange={toggleDisableVoucher}
            />
          </CellWrapper>
        </>
      )}
      <Wrapper>
        <Button
          tag="link_advanced_embedding"
          buttonModifiers={['buttonLink']}
          onClick={navigateToAdvancedEmbedding({ displayOauthSelect, displayAccessFeeSelect })}
        >
          Advanced embedding
        </Button>
      </Wrapper>
      {displayOauthSelect && (
        <>
          <Cell left={1} middle>
            OAuth app:
          </Cell>
          <Cell left={2}>
            <ExtendedDropdown
              tag="dropdown_oauth_app"
              fullWidth
              modifiers={['fontSizeMedium']}
              defaultOption={noneApp}
              options={apps}
              value={appId}
              onChange={onOauthAppSelected}
            />
          </Cell>
          {appSsoEnabled && (
            <CellWrapper>
              <Switch id="app-sso-switch" label="Enable SSO" checked={useSSO} onChange={onToggleUseSSO} />
            </CellWrapper>
          )}
        </>
      )}
      {displayAccessFeeSelect && (
        <>
          <Cell left={1} middle>
            Preselected price:
          </Cell>
          <Cell left={2}>
            <ExtendedDropdown
              tag="dropdown_preselected_prices"
              fullWidth
              modifiers={['fontSizeMedium']}
              defaultOption={noneAccessFee}
              options={accessPrices}
              value={preselectedFeeId ? preselectedFeeId.toString() : ''}
              onChange={onAccessFeeSelected}
            />
          </Cell>
        </>
      )}
    </Container>
  );
};

export default EmbedSettings;
